import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { BookOpen, GraduationCap, Certificate } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseCatalog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('PA');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [myApplicator, setMyApplicator] = useState(null);

  const fetchMyInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/my-credits`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setMyApplicator(data.applicator);
        setSelectedState(data.applicator.state);
      }
    } catch (error) {
      // User hasn't submitted license info yet
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/license-categories/${selectedState}`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [selectedState]);

  const fetchCourses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/courses?state_code=${selectedState}`;
      if (selectedCategory !== 'all') {
        url += `&license_category=${selectedCategory}`;
      }

      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch courses');
      
      const data = await res.json();
      setCourses(data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load courses');
      setLoading(false);
    }
  }, [selectedState, selectedCategory]);

  useEffect(() => {
    if (user) {
      fetchMyInfo();
    }
    fetchCategories();
    fetchCourses();
  }, [user, fetchMyInfo, fetchCategories, fetchCourses]);

  const handleEnroll = async (courseId) => {
    if (!user) {
      toast.error('Please log in to enroll in courses');
      navigate('/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/courses/enroll`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ course_id: courseId })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail);
      }

      toast.success('Enrolled successfully! View in My Courses');
      navigate('/my-courses');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      'CORE': 'bg-blue-100 text-blue-800',
      'TURF': 'bg-green-100 text-green-800',
      'AQUATIC': 'bg-cyan-100 text-cyan-800',
      'INDUSTRIAL': 'bg-orange-100 text-orange-800',
      'WOOD_DESTROYING': 'bg-amber-100 text-amber-800',
      'FUMIGATION': 'bg-red-100 text-red-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-text_dark">Loading courses...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <div className="bg-primary text-text_light py-12 border-b border-borders">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <GraduationCap size={48} weight="fill" className="text-accent" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              Course Catalog
            </h1>
          </div>
          <p className="text-base sm:text-lg max-w-3xl">
            State-specific continuing education courses. Only see courses approved for YOUR state and license categories.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-borders p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text_dark mb-2">
                State {myApplicator && <span className="text-xs text-text_muted">(Your state: {myApplicator.state})</span>}
              </label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="PA">Pennsylvania (PA)</option>
                <option value="OH">Ohio (OH)</option>
                <option value="VA">Virginia (VA)</option>
                <option value="WV">West Virginia (WV)</option>
                <option value="MD">Maryland (MD)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text_dark mb-2">License Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.category_code}>
                    {cat.category_name} ({cat.required_credits} credits required)
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!myApplicator && user && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 p-4 rounded-sm">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Submit your license information to see courses specifically for your state and track credits automatically. 
                <button
                  onClick={() => navigate('/license-renewal')}
                  className="ml-2 text-primary underline hover:no-underline"
                >
                  Submit License Info →
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Course Count */}
        <div className="mb-4 text-text_muted">
          Showing {courses.length} {selectedState} approved course{courses.length !== 1 ? 's' : ''}
          {selectedCategory !== 'all' && ` for ${selectedCategory} category`}
        </div>

        {/* Courses Grid */}
        {courses.length === 0 ? (
          <div className="text-center py-12 text-text_muted">
            No courses found for the selected filters. Check back soon!
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div key={course.id} className="bg-white rounded-lg shadow-sm border border-borders p-6 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <BookOpen size={32} className="text-primary" />
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                    {course.state_code}
                  </span>
                </div>

                <h3 className="font-bold text-lg text-text_dark mb-2">{course.title}</h3>
                <p className="text-sm text-text_muted mb-4 line-clamp-2">
                  {course.description}
                </p>

                <div className="mb-4 flex flex-wrap gap-1">
                  {course.license_categories.map(cat => (
                    <span
                      key={cat}
                      className={`text-xs px-2 py-1 rounded-full font-medium ${getCategoryBadgeColor(cat)}`}
                    >
                      {cat}: {course.credits_per_category[cat]} credit{course.credits_per_category[cat] > 1 ? 's' : ''}
                    </span>
                  ))}
                </div>

                <div className="text-xs text-text_muted mb-4 space-y-1">
                  <div>⏱️ Duration: {course.duration_hours} hours</div>
                  <div>📈 Level: {course.level}</div>
                  {course.instructor && <div>👨‍🏫 {course.instructor}</div>}
                  {course.state_approval_number && (
                    <div className="text-primary font-medium">
                      ✓ Approval: {course.state_approval_number}
                    </div>
                  )}
                </div>

                <div className="mt-auto pt-4 border-t border-borders">
                  <button
                    onClick={() => handleEnroll(course.id)}
                    className="w-full px-4 py-2 bg-primary text-white rounded-sm hover:bg-primary/90 transition-colors font-medium"
                  >
                    Enroll Now →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
